import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import React, { forwardRef, useId, useMemo } from 'react';
import { Spacing } from '../../foundations/spacing';
import { useDevice } from '../../providers/Device';
import { getTestIds } from '../../utils/TestIds';
import { Box, Column } from '../Layout';
import { TextField } from '../TextField';
import { Typography } from '../Typography';
import { useOTPVerificationField } from './hooks/useOTPVerificationField';
const DEFAULT_LABELS = {
    group: 'Verification code',
    digit: (position, total)=>"Digit ".concat(position, " of ").concat(total)
};
const DESKTOP_BOX_MAX_WIDTH = 56;
const MOBILE_BOX_MAX_WIDTH = 42;
export const GetOTPVerificationFieldTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            OTPVerificationFieldTestIds: {
                Digit: (index)=>"otp-verification-field-".concat(index)
            }
        }
    });
export const OTPVerificationField = /*#__PURE__*/ forwardRef((_0, _1)=>{
    let _ref = [
        _0,
        _1
    ], [_ref1, ..._rest] = _ref, { labels = DEFAULT_LABELS, length = 6, onChange, hasError = false, errorText, autoComplete, onInputChange, testId } = _ref1, biProps = _object_without_properties(_ref1, [
        "labels",
        "length",
        "onChange",
        "hasError",
        "errorText",
        "autoComplete",
        "onInputChange",
        "testId"
    ]), [ref] = _rest;
    const { OTPVerificationFieldTestIds } = useMemo(()=>GetOTPVerificationFieldTID(testId), [
        testId
    ]);
    const errorId = useId();
    const { values, error, inputsRef, handleChange, handleKeyDown, handlePaste } = useOTPVerificationField({
        length,
        hasError,
        onChange,
        onInputChange,
        ref
    });
    const { isMobileDevice } = useDevice();
    const boxMaxWidth = isMobileDevice ? MOBILE_BOX_MAX_WIDTH : DESKTOP_BOX_MAX_WIDTH;
    return /*#__PURE__*/ _jsxs(Column, {
        role: "group",
        "aria-label": labels.group,
        gap: Spacing.Space8,
        testId: OTPVerificationFieldTestIds.Group(),
        children: [
            /*#__PURE__*/ _jsx(Box, {
                justifyContent: "center",
                gap: 10,
                children: values.map((char, idx)=>/*#__PURE__*/ _createElement(TextField, _object_spread_props(_object_spread({}, biProps), {
                        collapseHelperText: false,
                        key: idx,
                        testId: OTPVerificationFieldTestIds.Digit(idx),
                        inputRef: (el)=>inputsRef.current[idx] = el,
                        value: char,
                        onChange: (e)=>handleChange(e, idx),
                        onPaste: handlePaste,
                        autoComplete: autoComplete,
                        error: error,
                        inputProps: {
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            'aria-label': labels.digit(idx + 1, length),
                            'aria-describedby': errorText ? errorId : undefined,
                            onKeyDown: (e)=>handleKeyDown(e, idx),
                            style: {
                                textAlign: 'center',
                                fontSize: '1.5rem',
                                padding: 0,
                                width: boxMaxWidth,
                                height: 56
                            }
                        },
                        style: {
                            width: "min(".concat(boxMaxWidth, "px, 100%)")
                        }
                    })))
            }),
            errorText && /*#__PURE__*/ _jsx(Typography, {
                id: errorId,
                role: "alert",
                skin: "error",
                variant: "paragraph2",
                align: "center",
                testId: OTPVerificationFieldTestIds.Error(),
                children: errorText
            })
        ]
    });
});
OTPVerificationField.displayName = 'OTPVerificationField';
